import { useTranslation } from 'react-i18next'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/utils/cn'

/** What the admin reads under the switch. */
export interface ExtensionSwitchNotice {
  text: string
  /**
   * The switch cannot move: the deployment or a required extension keeps the
   * extension off. False for a warning about what the change also does.
   */
  locks: boolean
}

interface ExtensionSwitchFieldProps {
  inputId: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  notice: ExtensionSwitchNotice | null
}

/** The admin toggle of one extension, with why it cannot move or what switching it also does. */
export function ExtensionSwitchField({
  inputId,
  checked,
  onCheckedChange,
  notice,
}: ExtensionSwitchFieldProps) {
  const { t } = useTranslation()
  const noticeId = `${inputId}-notice`
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={inputId} className="text-sm">
          {t('extensions.edit_dialog.enabled')}
        </Label>
        <Switch
          id={inputId}
          checked={checked}
          onCheckedChange={onCheckedChange}
          disabled={notice?.locks ?? false}
          aria-describedby={notice ? noticeId : undefined}
        />
      </div>
      {notice && (
        <p
          id={noticeId}
          role={notice.locks ? undefined : 'alert'}
          className={cn(
            'text-xs',
            notice.locks
              ? 'text-muted-foreground'
              : 'rounded-md border border-warning-border bg-warning-surface px-3 py-2 text-warning-foreground'
          )}
        >
          {notice.text}
        </p>
      )}
    </div>
  )
}
